/**
 * ReorderPageViewModel - Shared Reorder Page ViewModel
 *
 * Maps order history data to props for PageContentQuickReorder (all orders)
 * and PageContentReorderProducts (single order).
 *
 * Cross-platform implementation with dependency injection.
 */

import { useMemo } from 'react'
import type { IPlatformServices } from '../contracts'
import type {
  Order,
  OrderItem,
  SelectedOption,
} from '@restaurant/core'

import type {
  ButtonProductReorderProps,
  SectionReorderProps,
  PageContentQuickReorderProps,
  QuickReorderOrderData,
  PageContentReorderProductsProps,
} from '@restaurant/core'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats selected options into a human-readable string.
 * Each option group's selected items are joined, then groups are separated by "; ".
 *
 * Example: "Size: Large; Toppings: Extra Cheese, Mushrooms"
 */
function formatSelectedOptions(selectedOptions: SelectedOption[]): string | undefined {
  if (!selectedOptions || selectedOptions.length === 0) return undefined

  const parts = selectedOptions
    .map((opt) => {
      const items = opt.selectedItems.map((item) => item.name).join(', ')
      if (!items) return null
      return opt.optionTitle ? `${opt.optionTitle}: ${items}` : items
    })
    .filter(Boolean)

  return parts.length > 0 ? parts.join('; ') : undefined
}

/**
 * Derives an order number string from the order.
 * Prefers externalId; falls back to last 6 characters of the internal id.
 */
function getOrderNumber(order: Order): string {
  if (order.externalId) return order.externalId
  return order.id.slice(-6)
}

/**
 * Builds the title string for an order section.
 * "Pickup from <location>" or "Delivery to <street>".
 */
function getOrderTitle(order: Order, locationName: string): string {
  if (order.isDelivery && order.deliveryAddress?.street) {
    return `Delivery to ${order.deliveryAddress.street}`
  }
  return `Pickup from ${locationName}`
}

// ---------------------------------------------------------------------------
// Pure mapping functions (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Maps a single OrderItem to ButtonProductReorderProps.
 */
export function mapOrderItemToProduct(
  item: OrderItem,
  orderId: string,
  onProductPress?: (productId: string, orderId: string, itemId: string) => void,
): ButtonProductReorderProps {
  const productOptions = formatSelectedOptions(item.selectedOptions)

  return {
    productName: item.menuItem.name,
    productOptions,
    showProductOptions: Boolean(productOptions),
    showImageProductButton: Boolean(item.menuItem.imageUrl),
    imageUrl: item.menuItem.imageUrl ?? undefined,
    disabled: false,
    onPress: onProductPress
      ? () => onProductPress(item.menuItem.id, orderId, item.itemId)
      : undefined,
  }
}

/**
 * Maps an Order to a SectionReorderProps shape (used by both
 * QuickReorderOrderData and PageContentReorderProductsProps).
 */
export function mapOrderToReorderProps(
  order: Order,
  locationName: string,
  callbacks?: {
    onProductPress?: (productId: string, orderId: string, itemId: string) => void
    onAddAllPress?: (orderId: string) => void
    onDetailsPress?: (orderId: string) => void
  },
): SectionReorderProps {
  const products = order.items.map((item) =>
    mapOrderItemToProduct(item, order.id, callbacks?.onProductPress),
  )

  return {
    orderNumber: getOrderNumber(order),
    title: getOrderTitle(order, locationName),
    products,
    onAddAllPress: callbacks?.onAddAllPress
      ? () => callbacks.onAddAllPress!(order.id)
      : undefined,
    onDetailsPress: callbacks?.onDetailsPress
      ? () => callbacks.onDetailsPress!(order.id)
      : undefined,
  }
}

// ---------------------------------------------------------------------------
// Dependencies & return types
// ---------------------------------------------------------------------------

export interface ReorderPageViewModelDependencies {
  /** Hook that returns order history data */
  useOrderData: () => { orders: Order[]; loading: boolean }
  /** Resolves a merchantId to a human-readable location name */
  getLocationName: (merchantId: string) => string
  /** Looks up a single order by id */
  getOrderById: (orderId: string) => Order | undefined
  /** Called when a product button is pressed */
  onProductPress?: (productId: string, orderId: string, itemId: string) => void
  /** Called when "Add All Items" is pressed for an order */
  onAddAllPress?: (orderId: string) => void
  /** Called when "Order Details" is pressed for an order */
  onDetailsPress?: (orderId: string) => void
}

export interface ReorderPageViewModelReturn {
  /** Props for PageContentQuickReorder (all orders) */
  getQuickReorderProps: () => Partial<PageContentQuickReorderProps>
  /** Props for PageContentReorderProducts (single order) */
  getSingleOrderReorderProps: (orderId: string) => Partial<PageContentReorderProductsProps> | null
  /** Whether order data is loading */
  loading: boolean
  /** Raw orders from the injected hook */
  orders: Order[]
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Factory that returns a React hook for the Reorder page.
 *
 * Usage:
 * ```ts
 * const useReorderPage = createReorderPageViewModel(services, deps)
 * // inside a component:
 * const vm = useReorderPage()
 * ```
 */
export const createReorderPageViewModel = (
  _services: IPlatformServices,
  dependencies: ReorderPageViewModelDependencies,
) => {
  return (): ReorderPageViewModelReturn => {
    const {
      useOrderData,
      getLocationName,
      getOrderById,
      onProductPress,
      onAddAllPress,
      onDetailsPress,
    } = dependencies

    // Invoke the injected hook to get order data
    const { orders, loading } = useOrderData()

    const callbacks = useMemo(
      () => ({ onProductPress, onAddAllPress, onDetailsPress }),
      [onProductPress, onAddAllPress, onDetailsPress],
    )

    // ------------------------------------------------------------------
    // Prop getters
    // ------------------------------------------------------------------

    const getQuickReorderProps = useMemo(
      () => (): Partial<PageContentQuickReorderProps> => {
        const mappedOrders: QuickReorderOrderData[] = orders
          .filter((order) => order.items.length > 0)
          .map((order) => ({
            id: order.id,
            ...mapOrderToReorderProps(
              order,
              getLocationName(order.merchantId),
              callbacks,
            ),
          }))

        return {
          orders: mappedOrders,
          loading,
        }
      },
      [orders, loading, getLocationName, callbacks],
    )

    const getSingleOrderReorderProps = useMemo(
      () =>
        (orderId: string): Partial<PageContentReorderProductsProps> | null => {
          const order = getOrderById(orderId)
          if (!order) return null

          const props = mapOrderToReorderProps(
            order,
            getLocationName(order.merchantId),
            callbacks,
          )

          // PageContentReorderProductsProps extends SectionReorderProps
          // (omitting className/style/testID/accessibilityLabel), so we can spread directly.
          return { ...props }
        },
      [getOrderById, getLocationName, callbacks],
    )

    return {
      getQuickReorderProps,
      getSingleOrderReorderProps,
      loading,
      orders,
    }
  }
}
